// Metro Roadmap - Main Component
// Visualizes roadmaps as a subway/metro map with lines, stations, and connections

import { useState, useEffect, useCallback, useMemo } from 'react';
import Xarrow from 'react-xarrows';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Link, Edit2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { metroApi } from '../shared/api';
import { useRoadmap } from '../shared/RoadmapContext';
import { MONTHS, STATUS_COLORS, METRO_LINE_COLORS } from '../shared/constants';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import { LoadingState, ErrorBanner } from '@/components/ds';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ZoomIn, ZoomOut, ChevronDown } from 'lucide-react';
import { useIsMobile } from '@/hooks/useMobile';
import type { MetroLine, MetroStation, MetroConnection, RoadmapStatus } from '../shared/types';

interface MetroRoadmapProps {
    year?: number;
}

export function MetroRoadmap({ year: initialYear = new Date().getFullYear() }: MetroRoadmapProps) {
    const { user } = useAuth();
    const { roadmap } = useRoadmap();
    const isMobile = useIsMobile();
    const [year, setYear] = useState(initialYear);
    const [legendOpen, setLegendOpen] = useState(false);
    const [lines, setLines] = useState<MetroLine[]>([]);
    const [stations, setStations] = useState<MetroStation[]>([]);
    const [connections, setConnections] = useState<MetroConnection[]>([]);
    const [loading, setLoading] = useState(true);
    // METRO-11: surface load errors instead of silently rendering an empty grid
    const [loadError, setLoadError] = useState<string | null>(null);
    const [selectedStation, setSelectedStation] = useState<MetroStation | null>(null);
    const [editingStationName, setEditingStationName] = useState('');
    const [editingDescription, setEditingDescription] = useState('');
    const [editingLine, setEditingLine] = useState<MetroLine | null>(null);
    const [editingLineName, setEditingLineName] = useState('');
    const [isAddingLine, setIsAddingLine] = useState(false);
    const [isAddingStation, setIsAddingStation] = useState(false);
    const [connectingSourceId, setConnectingSourceId] = useState<string | null>(null);
    const [newLineName, setNewLineName] = useState('');
    const [newLineColor, setNewLineColor] = useState(METRO_LINE_COLORS[0]);
    const [newStation, setNewStation] = useState({ lineId: '', monthIndex: 0, name: '' });
    const [pendingModalClose, setPendingModalClose] = useState<'line' | 'station' | null>(null);
    const [zoom, setZoom] = useState(1);

    // Initial state to check for dirty forms
    const isLineDirty = newLineName !== '';
    const isStationDirty = newStation.name !== '' || newStation.lineId !== '';

    // Fetch data
    const loadData = useCallback(async () => {
        if (!user || !roadmap) return;
        setLoading(true);
        setLoadError(null);
        try {
            const linesRes = await metroApi.listLines(roadmap.id);
            if (linesRes.error) throw linesRes.error;
            const lineData = linesRes.data || [];
            setLines(lineData);

            // Get all stations for all lines
            const allStations: MetroStation[] = [];
            for (const line of lineData) {
                const stationsRes = await metroApi.listStations(line.id);
                if (stationsRes.error) throw stationsRes.error;
                if (stationsRes.data) {
                    allStations.push(...stationsRes.data.filter(s => s.year === year));
                }
            }
            setStations(allStations);

            // Get connections
            if (allStations.length > 0) {
                const connectionsRes = await metroApi.listConnections(allStations.map(s => s.id));
                if (connectionsRes.error) throw connectionsRes.error;
                setConnections(connectionsRes.data || []);
            }
        } catch (error) {
            // METRO-11: keep the console log, but also surface a user-visible banner
            console.error('Failed to load metro data:', error);
            const message = error instanceof Error ? error.message : 'Failed to load roadmap data.';
            setLoadError(message);
        } finally {
            setLoading(false);
        }
    }, [user, roadmap, year]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Sync editing name when selected station changes (METRO-4)
    useEffect(() => {
        setEditingStationName(selectedStation?.name ?? '');
        setEditingDescription(selectedStation?.description ?? '');
    }, [selectedStation]);

    // METRO-9: Dynamic year range based on current year
    const availableYears = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const start = Math.min(currentYear - 1, year - 1);
        const end = Math.max(currentYear + 3, year + 2);
        const years: number[] = [];
        for (let y = start; y <= end; y++) years.push(y);
        return years;
    }, [year]);

    // Group stations by line
    const stationsByLine = useMemo(() => {
        const grouped: Record<string, MetroStation[]> = {};
        for (const station of stations) {
            if (!grouped[station.line_id]) grouped[station.line_id] = [];
            grouped[station.line_id].push(station);
        }
        return grouped;
    }, [stations]);

    // Add new line
    const handleAddLine = async () => {
        if (!newLineName.trim()) {
            toast.error("Please enter a line name");
            return;
        }
        if (!user || !roadmap) return;

        // Calculate next order index
        const nextOrderIndex = lines.length > 0
            ? Math.max(...lines.map(l => l.order_index)) + 1
            : 0;

        await metroApi.createLine({
            owner_id: user.id,
            roadmap_id: roadmap.id,
            name: newLineName,
            color: newLineColor,
            order_index: nextOrderIndex,
        });
        setNewLineName('');
        setIsAddingLine(false);
        loadData();
    };

    // Add new station
    const handleAddStation = async () => {
        if (!newStation.lineId) {
            toast.error("Please select a line");
            return;
        }
        if (!newStation.name.trim()) {
            toast.error("Please enter a station name");
            return;
        }
        // METRO-1: Prevent same-line same-month collision
        const collision = stationsByLine[newStation.lineId]?.find(s => s.month_index === newStation.monthIndex);
        if (collision) {
            toast.error(`This line already has a station in ${MONTHS[newStation.monthIndex]}`);
            return;
        }
        await metroApi.createStation({
            line_id: newStation.lineId,
            year,
            month_index: newStation.monthIndex,
            name: newStation.name,
        });
        setNewStation({ lineId: '', monthIndex: 0, name: '' });
        setIsAddingStation(false);
        loadData();
    };

    // Update station status
    const handleStatusChange = async (stationId: string, status: RoadmapStatus) => {
        await metroApi.updateStation(stationId, { status });
        loadData();
    };

    // Create connection
    const handleConnect = async (targetId: string) => {
        if (!connectingSourceId) return;
        // METRO-2: Prevent connecting stations on the same line (metro interchanges are cross-line)
        const sourceStation = stations.find(s => s.id === connectingSourceId);
        const targetStation = stations.find(s => s.id === targetId);
        if (sourceStation?.line_id === targetStation?.line_id) {
            toast.error('Connections must cross lines — stations on the same line are already linked by the rail');
            setConnectingSourceId(null);
            return;
        }
        // Prevent duplicate connections
        const alreadyConnected = connections.some(c =>
            (c.source_station_id === connectingSourceId && c.target_station_id === targetId) ||
            (c.source_station_id === targetId && c.target_station_id === connectingSourceId)
        );
        if (alreadyConnected) {
            toast.error('These stations are already connected');
            setConnectingSourceId(null);
            return;
        }
        await metroApi.createConnection({
            source_station_id: connectingSourceId,
            target_station_id: targetId,
            curve_type: 'bezier',
        });
        setConnectingSourceId(null);
        loadData();
    };

    // Rename selected station (METRO-4 + METRO-6: update selectedStation state optimistically)
    const handleRenameStation = async () => {
        if (!selectedStation || !editingStationName.trim() || editingStationName === selectedStation.name) return;
        const trimmed = editingStationName.trim();
        await metroApi.updateStation(selectedStation.id, { name: trimmed });
        // METRO-6: update selectedStation so header immediately shows new name
        setSelectedStation(prev => prev ? { ...prev, name: trimmed } : null);
        loadData();
        toast.success('Station renamed');
    };

    // METRO-8: Save station description
    const handleSaveDescription = async () => {
        if (!selectedStation || editingDescription === (selectedStation.description ?? '')) return;
        await metroApi.updateStation(selectedStation.id, { description: editingDescription });
        setSelectedStation(prev => prev ? { ...prev, description: editingDescription } : null);
        loadData();
    };

    // METRO-7: Update line name
    const handleUpdateLine = async () => {
        if (!editingLine || !editingLineName.trim()) return;
        await metroApi.updateLine(editingLine.id, { name: editingLineName.trim() });
        setEditingLine(null);
        loadData();
        toast.success('Line renamed');
    };

    // METRO-7: Delete line
    const handleDeleteLine = async (lineId: string) => {
        await metroApi.deleteLine(lineId);
        if (selectedStation?.line_id === lineId) setSelectedStation(null);
        setEditingLine(null);
        loadData();
        toast.success('Line deleted');
    };

    // METRO-5: Delete a connection
    const handleDeleteConnection = async (connectionId: string) => {
        await metroApi.deleteConnection(connectionId);
        loadData();
        toast.success('Connection removed');
    };

    // Delete station
    const handleDeleteStation = async (stationId: string) => {
        await metroApi.deleteStation(stationId);
        setSelectedStation(null);
        loadData();
    };

    if (loading) {
        return <LoadingState />;
    }

    // METRO-11: surface load errors instead of silently rendering an empty grid
    if (loadError) {
        return (
            <div className="p-6 space-y-3">
                <ErrorBanner message={`Failed to load metro roadmap: ${loadError}`} />
                <Button variant="outline" size="sm" onClick={() => loadData()}>
                    Retry
                </Button>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <h2 className="text-lg md:text-2xl font-bold">Metro Roadmap</h2>
                    <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
                        <SelectTrigger className="w-[90px] md:w-[100px] h-9">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {availableYears.map((y) => (
                                <SelectItem key={y} value={String(y)}>
                                    {y}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex gap-2">
                    {/* Add Line Button */}
                    <Dialog
                        open={isAddingLine}
                        onOpenChange={(open) => {
                            if (!open && isLineDirty) {
                                setPendingModalClose('line');
                            } else {
                                setIsAddingLine(open);
                            }
                        }}
                    >
                        <DialogTrigger asChild>
                            <Button variant="outline" size="sm">
                                <Plus className="h-4 w-4 mr-2" />
                                Add Line
                            </Button>
                        </DialogTrigger>
                        <DialogContent aria-describedby={undefined}>
                            <DialogHeader>
                                <DialogTitle>Add New Line</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div>
                                    <Label>Line Name</Label>
                                    <Input
                                        value={newLineName}
                                        onChange={(e) => setNewLineName(e.target.value)}
                                        placeholder="e.g., Product Development"
                                    />
                                </div>
                                <div>
                                    <Label>Color</Label>
                                    <div className="flex gap-2 flex-wrap mt-2">
                                        {METRO_LINE_COLORS.map((color) => (
                                            <button
                                                key={color}
                                                className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all ${newLineColor === color ? 'border-primary ring-2 ring-primary ring-offset-2' : 'border-transparent hover:scale-110'
                                                    }`}
                                                style={{ backgroundColor: color }}
                                                onClick={() => setNewLineColor(color)}
                                            >
                                                {newLineColor === color && <Check className="h-4 w-4 text-white drop-shadow-md" />}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <Button onClick={handleAddLine} className="w-full">
                                    Create Line
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>

                    {/* Add Station Button */}
                    <Dialog
                        open={isAddingStation}
                        onOpenChange={(open) => {
                            if (!open && isStationDirty) {
                                setPendingModalClose('station');
                            } else {
                                setIsAddingStation(open);
                            }
                        }}
                    >
                        <DialogTrigger asChild>
                            <Button size="sm">
                                <Plus className="h-4 w-4 mr-2" />
                                Add Station
                            </Button>
                        </DialogTrigger>
                        <DialogContent aria-describedby={undefined}>
                            <DialogHeader>
                                <DialogTitle>Add New Station</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div>
                                    <Label>Line</Label>
                                    <Select value={newStation.lineId} onValueChange={(v) => setNewStation({ ...newStation, lineId: v })}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select a line" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {lines.map((line) => (
                                                <SelectItem key={line.id} value={line.id}>
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: line.color }} />
                                                        {line.name}
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Month</Label>
                                    <Select
                                        value={String(newStation.monthIndex)}
                                        onValueChange={(v) => setNewStation({ ...newStation, monthIndex: parseInt(v) })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {MONTHS.map((month, i) => (
                                                <SelectItem key={i} value={String(i)}>
                                                    {month}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Station Name</Label>
                                    <Input
                                        value={newStation.name}
                                        onChange={(e) => setNewStation({ ...newStation, name: e.target.value })}
                                        placeholder="e.g., MVP Launch"
                                    />
                                </div>
                                <Button onClick={handleAddStation} className="w-full">
                                    Add Station
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>



            {/* Controls Bar & Legend */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2">
                {/* Status Legend - Collapsible on mobile */}
                {isMobile ? (
                    <Collapsible open={legendOpen} onOpenChange={setLegendOpen}>
                        <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="flex items-center gap-2 h-8 px-2">
                                <span className="text-sm font-semibold">Legend</span>
                                <ChevronDown className={`h-4 w-4 transition-transform ${legendOpen ? 'rotate-180' : ''}`} />
                            </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="pt-2">
                            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                                {Object.entries(STATUS_COLORS).map(([status, color]) => (
                                    <div key={status} className="flex items-center gap-1">
                                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                                        <span>{status.replace('_', ' ')}</span>
                                    </div>
                                ))}
                            </div>
                        </CollapsibleContent>
                    </Collapsible>
                ) : (
                    <div className="flex gap-4 items-center text-sm text-muted-foreground shrink-0">
                        <span className="font-semibold">Status:</span>
                        {Object.entries(STATUS_COLORS).map(([status, color]) => (
                            <div key={status} className="flex items-center gap-1">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                                <span>{status.replace('_', ' ')}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Zoom Controls */}
                <div className="flex items-center gap-2">
                    <Button aria-label="Zoom out" variant="outline" size="icon" className="h-9 w-9" onClick={() => setZoom(Math.max(0.5, zoom - 0.1))} disabled={zoom <= 0.5}>
                        <ZoomOut className="h-4 w-4" />
                    </Button>
                    <span className="w-12 text-center text-sm">{Math.round(zoom * 100)}%</span>
                    <Button aria-label="Zoom in" variant="outline" size="icon" className="h-9 w-9" onClick={() => setZoom(Math.min(1.5, zoom + 0.1))} disabled={zoom >= 1.5}>
                        <ZoomIn className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* Confirmation Dialog for Unsaved Changes */}
            {/* Confirmation Dialog for Unsaved Changes */}
            <AlertDialog open={!!pendingModalClose} onOpenChange={(open) => !open && setPendingModalClose(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
                        <AlertDialogDescription>
                            You have unsaved changes. Are you sure you want to close this modal?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => {
                            if (pendingModalClose === 'line') {
                                setIsAddingLine(false);
                                setNewLineName('');
                            } else {
                                setIsAddingStation(false);
                                setNewStation({ lineId: '', monthIndex: 0, name: '' });
                            }
                            setPendingModalClose(null);
                        }}>
                            Discard Changes
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* METRO-7: Line edit dialog */}
            <Dialog open={!!editingLine} onOpenChange={(open) => !open && setEditingLine(null)}>
                <DialogContent aria-describedby={undefined}>
                    <DialogHeader>
                        <DialogTitle>Edit Line</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Line Name</Label>
                            <Input
                                value={editingLineName}
                                onChange={(e) => setEditingLineName(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleUpdateLine()}
                                autoFocus
                            />
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button
                            variant="destructive"
                            className="mr-auto"
                            onClick={() => editingLine && handleDeleteLine(editingLine.id)}
                        >
                            <Trash2 className="h-4 w-4 mr-2" /> Delete Line
                        </Button>
                        <Button variant="outline" onClick={() => setEditingLine(null)}>Cancel</Button>
                        <Button onClick={handleUpdateLine} disabled={!editingLineName.trim()}>Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Metro Map Grid */}
            <Card className="p-4 overflow-auto">
                <div style={{ transform: `scale(${zoom})`, transformOrigin: 'top left', transition: 'transform 0.2s' }}>

                    {/* Month Headers */}
                    <div className="flex min-w-max">
                        <div className="w-32 shrink-0" /> {/* Line label column */}
                        {MONTHS.map((month, i) => (
                            <div
                                key={i}
                                className="flex-1 text-center text-sm font-medium text-muted-foreground border-l border-dashed border-muted py-2"
                            >
                                {month}
                            </div>
                        ))}
                    </div>

                    {/* Lines and Stations */}
                    <div className="min-w-max relative">
                        {lines.map((line) => (
                            <div key={line.id} className="flex items-center h-24 border-t border-muted group transition-colors hover:bg-muted/30">
                                {/* Line Label (METRO-7: hover label to reveal edit button) */}
                                <div className="w-32 shrink-0 flex items-center gap-1 px-2 group/linelabel">
                                    <div className="w-4 h-4 rounded-full shrink-0" style={{ backgroundColor: line.color }} />
                                    <span className="text-sm font-medium truncate group-hover/linelabel:text-primary transition-colors flex-1" title={line.name}>{line.name}</span>
                                    <button
                                        className="opacity-0 group-hover/linelabel:opacity-100 transition-opacity p-0.5 hover:text-primary rounded"
                                        onClick={() => { setEditingLine(line); setEditingLineName(line.name); }}
                                        title="Rename line"
                                    >
                                        <Edit2 className="h-3 w-3" />
                                    </button>
                                </div>

                                {/* Station Grid */}
                                <div className="flex-1 flex relative">
                                    {/* Horizontal line through center */}
                                    <div
                                        className="absolute left-0 right-0 top-1/2 h-1 -translate-y-1/2"
                                        style={{ backgroundColor: line.color }}
                                    />

                                    {/* Month columns */}
                                    {MONTHS.map((_, monthIndex) => {
                                        const station = stationsByLine[line.id]?.find(s => s.month_index === monthIndex);
                                        return (
                                            <div
                                                key={monthIndex}
                                                className="flex-1 flex items-center justify-center border-l border-dashed border-muted relative group/cell"
                                            >
                                                {station ? (
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <div
                                                                    id={`station-${station.id}`}
                                                                    className={`${isMobile ? 'w-10 h-10 border-[5px]' : 'w-6 h-6 border-4'} rounded-full cursor-pointer z-10 transition-transform hover:scale-125 ${connectingSourceId === station.id ? 'ring-2 ring-offset-2 ring-primary' : ''
                                                                        } ${selectedStation?.id === station.id ? 'scale-125' : ''}`}
                                                                    style={{
                                                                        backgroundColor: 'white',
                                                                        borderColor: STATUS_COLORS[station.status],
                                                                    }}
                                                                    onClick={() => {
                                                                        if (connectingSourceId && connectingSourceId !== station.id) {
                                                                            handleConnect(station.id);
                                                                        } else {
                                                                            setSelectedStation(station);
                                                                        }
                                                                    }}
                                                                />
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p className="font-semibold">{station.name}</p>
                                                                <p className="text-xs text-muted-foreground">{STATUS_COLORS[station.status] ? station.status.replace('_', ' ') : ''}</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                ) : null}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}

                        {/* Connections (Xarrows) — METRO-3: only render when both endpoints are visible in this year */}
                        {connections
                            .filter(conn =>
                                stations.some(s => s.id === conn.source_station_id) &&
                                stations.some(s => s.id === conn.target_station_id)
                            )
                            .map((conn) => (
                            <Xarrow
                                key={conn.id}
                                start={`station-${conn.source_station_id}`}
                                end={`station-${conn.target_station_id}`}
                                color="#6b7280"
                                strokeWidth={2}
                                path={conn.curve_type === 'bezier' ? 'smooth' : conn.curve_type === 'step' ? 'grid' : 'straight'}
                                curveness={0.3}
                                dashness={false}
                                showHead={true}
                                headSize={4}
                            />
                        ))}

                    </div>

                </div>
            </Card>

            {/* Station Details Panel */}
            {
                selectedStation && (
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold">{selectedStation.name}</h3>
                            <div className="flex gap-2">
                                <Button
                                    variant={connectingSourceId === selectedStation.id ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => {
                                        if (connectingSourceId === selectedStation.id) {
                                            setConnectingSourceId(null);
                                        } else {
                                            setConnectingSourceId(selectedStation.id);
                                            // METRO-10: Inform user to click target
                                            toast.info('Click a station on a different line to connect', { duration: 4000 });
                                        }
                                    }}
                                >
                                    <Link className="h-4 w-4 mr-2" />
                                    {connectingSourceId === selectedStation.id ? 'Click target...' : 'Connect'}
                                </Button>
                                <Button variant="destructive" size="sm" onClick={() => handleDeleteStation(selectedStation.id)}>
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label>Station Name</Label>
                                <div className="flex gap-2 mt-1">
                                    <Input
                                        value={editingStationName}
                                        onChange={(e) => setEditingStationName(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleRenameStation()}
                                    />
                                    <Button
                                        size="sm"
                                        onClick={handleRenameStation}
                                        disabled={!editingStationName.trim() || editingStationName === selectedStation.name}
                                    >
                                        Save
                                    </Button>
                                </div>
                            </div>
                            <div>
                                <Label>Status</Label>
                                <Select
                                    value={selectedStation.status}
                                    onValueChange={(v) => handleStatusChange(selectedStation.id, v as RoadmapStatus)}
                                >
                                    <SelectTrigger className="text-base">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.keys(STATUS_COLORS).map((status) => (
                                            <SelectItem key={status} value={status}>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: STATUS_COLORS[status] }} />
                                                    {status.replace('_', ' ')}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Month</Label>
                                <div className="text-sm text-muted-foreground mt-2">
                                    {MONTHS[selectedStation.month_index]} {selectedStation.year}
                                </div>
                            </div>
                            {/* METRO-8: Description textarea */}
                            <div className="sm:col-span-2">
                                <Label>Description</Label>
                                <Textarea
                                    className="mt-1 text-sm min-h-[60px]"
                                    placeholder="Add a description..."
                                    value={editingDescription}
                                    onChange={(e) => setEditingDescription(e.target.value)}
                                    onBlur={handleSaveDescription}
                                />
                            </div>
                        </div>

                        {/* METRO-5: Connections list */}
                        {(() => {
                            const stationConns = connections.filter(c =>
                                c.source_station_id === selectedStation.id || c.target_station_id === selectedStation.id
                            );
                            if (stationConns.length === 0) return null;
                            return (
                                <div className="mt-4 border-t pt-4">
                                    <Label className="mb-2 block">Connections ({stationConns.length})</Label>
                                    <div className="space-y-1">
                                        {stationConns.map(conn => {
                                            const otherId = conn.source_station_id === selectedStation.id
                                                ? conn.target_station_id : conn.source_station_id;
                                            const other = stations.find(s => s.id === otherId);
                                            return (
                                                <div key={conn.id} className="flex items-center justify-between text-sm">
                                                    <span className="text-muted-foreground">
                                                        ↔ {other?.name ?? 'Unknown station'}
                                                    </span>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-6 text-destructive hover:text-destructive hover:bg-destructive/10 px-2"
                                                        onClick={() => handleDeleteConnection(conn.id)}
                                                    >
                                                        <Trash2 className="h-3 w-3" />
                                                    </Button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })()}
                    </Card>
                )
            }

            {/* Empty State */}
            {
                lines.length === 0 && (
                    <Card className="p-12 text-center text-muted-foreground flex flex-col items-center justify-center space-y-4">
                        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-2">
                            <Plus className="w-8 h-8 opacity-50" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground">No Lines Created Yet</h3>
                        <p className="max-w-md mx-auto">
                            Get started by adding your first metro line. Lines represent different tracks or streams of work in your roadmap.
                        </p>
                        <Button onClick={() => setIsAddingLine(true)} className="mt-4">
                            Add First Line
                        </Button>
                    </Card>
                )
            }
        </div>
    );
}

export default MetroRoadmap;
